import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Table, Tag, Space, Button, Drawer, Form, Input, Select, DatePicker,
  InputNumber, Typography, message, Alert, Card, Row, Col,
  Popconfirm, Radio, Tooltip, Descriptions, Modal,
} from 'antd';
import {
  PlusOutlined, ExportOutlined, EyeOutlined, EditOutlined,
  CloseCircleOutlined, SearchOutlined, ReloadOutlined, FileAddOutlined, GlobalOutlined,
} from '@ant-design/icons';
import {
  COUPON_TYPES, COUPON_STATUS, CODE_STATUS, VOIDABLE_CODE_STATUS,
  MOCK_PACKAGES, getPackage, genCouponCode, resolveCouponStatus,
  MOCK_COUPONS, MOCK_CODES, DOMAIN_NODES, getDomainNode,
  COUPON_LANGS, countI18n, getI18nLabels,
} from './couponData';
import CouponNameI18nModal from './components/CouponNameI18nModal';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const CouponList = () => {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [makeForm] = Form.useForm();
  const [coupons, setCoupons] = useState(MOCK_COUPONS);
  const [codes, setCodes] = useState(MOCK_CODES);
  const [filters, setFilters] = useState({});

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [makeVisible, setMakeVisible] = useState(false);
  const [makeCoupon, setMakeCoupon] = useState(null);
  const [codesVisible, setCodesVisible] = useState(false);
  const [codesCoupon, setCodesCoupon] = useState(null);
  const [codeNodeFilter, setCodeNodeFilter] = useState();
  const [selectedCodeKeys, setSelectedCodeKeys] = useState([]);

  // 券名称多语言：i18nTarget 为空 = 新建/编辑抽屉内配置；有值 = 列表券名称列快捷修改
  const [formI18n, setFormI18n] = useState({});
  const [i18nOpen, setI18nOpen] = useState(false);
  const [i18nTarget, setI18nTarget] = useState(null);

  const couponType = Form.useWatch('type', form);
  const packageId = Form.useWatch('packageId', form);
  const couponFormName = Form.useWatch('name', form);
  const selectedPackage = getPackage(packageId);

  const handleSearch = () => {
    setFilters(searchForm.getFieldsValue());
  };

  const handleResetSearch = () => {
    searchForm.resetFields();
    setFilters({});
  };

  // 按查询条件过滤
  const filteredCoupons = coupons.filter((c) => {
    const { name, id, code, type, domainNode, status, validity } = filters;
    if (name) {
      const kw = String(name).trim().toLowerCase();
      // 中文名称或多语言译文任一命中即可
      const i18nHit = Object.values(c.i18n || {}).some(
        (v) => v && String(v).trim().toLowerCase().includes(kw),
      );
      if (!c.name.toLowerCase().includes(kw) && !i18nHit) return false;
    }
    if (id) {
      const kw = String(id).trim().toLowerCase();
      if (!c.id.toLowerCase().includes(kw)) return false;
    }
    if (code) {
      // 券码反查：该券码归属于哪些卡券
      const kw = String(code).trim().toLowerCase();
      const hit = codes.some((cd) => cd.couponId === c.id && cd.code.toLowerCase().includes(kw));
      if (!hit) return false;
    }
    if (type && c.type !== type) return false;
    if (domainNode) {
      // 域节点筛选：该卡券下存在属于该域节点的券码即命中
      const hit = codes.some((cd) => cd.couponId === c.id && cd.domainNode === domainNode);
      if (!hit) return false;
    }
    if (status && resolveCouponStatus(c) !== status) return false;
    if (validity && validity.length === 2 && validity[0] && validity[1]) {
      const start = validity[0].format('YYYY-MM-DD');
      const end = validity[1].format('YYYY-MM-DD');
      // 有效期与查询区间有交集
      if (c.endDate < start || c.startDate > end) return false;
    }
    return true;
  });

  const currentCodes = codes.filter(
    (c) => c.couponId === codesCoupon?.id && (!codeNodeFilter || c.domainNode === codeNodeFilter),
  );

  const openCreate = () => {
    setEditingCoupon(null);
    setDrawerVisible(true);
    form.resetFields();
    setFormI18n({});
  };

  const openEdit = (record) => {
    setEditingCoupon(record);
    setDrawerVisible(true);
    form.resetFields();
    setFormI18n(record.i18n || {});
    form.setFieldsValue({
      name: record.name,
      type: record.type,
      packageId: record.packageId,
      validity: record.startDate && record.endDate
        ? [dayjs(record.startDate), dayjs(record.endDate)]
        : undefined,
      discountRate: record.discountRate,
      maxDeduct: record.maxDeduct,
      cnThreshold: record.cnThreshold,
      cnCut: record.cnCut,
      usdThreshold: record.usdThreshold,
      usdCut: record.usdCut,
    });
  };

  const openMake = (record) => {
    setMakeCoupon(record);
    setMakeVisible(true);
    makeForm.resetFields();
  };

  const openCodes = (record) => {
    setCodesCoupon(record);
    setCodeNodeFilter(undefined);
    setSelectedCodeKeys([]);
    setCodesVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        i18n: formI18n,
        type: values.type,
        packageId: values.packageId || null,
        startDate: values.validity[0].format('YYYY-MM-DD'),
        endDate: values.validity[1].format('YYYY-MM-DD'),
        discountRate: values.type === 'discount' ? values.discountRate : undefined,
        maxDeduct: values.type === 'discount' ? values.maxDeduct : undefined,
        cnThreshold: values.type === 'fullcut' ? values.cnThreshold : undefined,
        cnCut: values.type === 'fullcut' ? values.cnCut : undefined,
        usdThreshold: values.type === 'fullcut' ? values.usdThreshold : undefined,
        usdCut: values.type === 'fullcut' ? values.usdCut : undefined,
      };
      if (editingCoupon) {
        setCoupons((prev) => prev.map((c) => (c.id === editingCoupon.id ? { ...c, ...payload } : c)));
        setEditingCoupon((prev) => ({ ...prev, ...payload }));
      } else {
        const newCoupon = {
          key: String(Date.now()),
          id: `CPN${dayjs().format('YYYYMMDD')}${String(Math.floor(Math.random() * 9000) + 1000)}`,
          made: 0,
          issued: 0,
          used: 0,
          status: 'available',
          ...payload,
        };
        setCoupons((prev) => [newCoupon, ...prev]);
      }
      message.success(editingCoupon ? '卡券已更新' : '卡券创建成功');
      setDrawerVisible(false);
    } catch (e) {
      // 校验失败由表单提示
    }
  };

  // 打开券名称多语言弹窗：i18nTarget 为空 = 新建/编辑抽屉内配置
  const openI18n = (record) => {
    setI18nTarget(record || null);
    setI18nOpen(true);
  };

  const closeI18n = () => {
    setI18nOpen(false);
    setI18nTarget(null);
  };

  const handleI18nSave = (nextI18n) => {
    if (i18nTarget) {
      setCoupons((prev) => prev.map((c) => (c.id === i18nTarget.id ? { ...c, i18n: nextI18n } : c)));
      message.success(`「${i18nTarget.name}」名称多语言已更新（已配置 ${countI18n(nextI18n)}/${COUPON_LANGS.length}）`);
    } else {
      setFormI18n(nextI18n);
      message.success(`多语言已暂存（已配置 ${countI18n(nextI18n)}/${COUPON_LANGS.length}），保存卡券后生效`);
    }
    closeI18n();
  };

  // 制券：生成券码池（原型最多生成 100 条明细用于演示）
  const handleMake = async () => {
    try {
      const values = await makeForm.validateFields();
      const count = values.count;
      const demoCount = Math.min(count, 100);
      const baseKey = `${makeCoupon.id}-${Date.now()}`;
      const newCodes = Array.from({ length: demoCount }).map((_, i) => ({
        key: `${baseKey}-${i}`,
        couponId: makeCoupon.id,
        domainNode: values.domainNode,
        code: genCouponCode(),
        target: '-',
        targetType: '-',
        issuedAt: '-',
        status: 'unissued',
      }));
      setCodes((prev) => [...newCodes, ...prev]);
      setCoupons((prev) => prev.map((c) => (c.id === makeCoupon.id ? { ...c, made: c.made + count } : c)));
      message.success(`已生成 ${count} 张券码，可在「券码」明细中查看未发放券码`);
      setMakeVisible(false);
    } catch (e) {
      // 校验失败由表单提示
    }
  };

  const handleVoidCoupon = (record) => {
    setCoupons((prev) => prev.map((c) => (c.id === record.id ? { ...c, status: 'voided' } : c)));
    setCodes((prev) => prev.map((c) => (c.couponId === record.id && VOIDABLE_CODE_STATUS.includes(c.status) ? { ...c, status: 'voided' } : c)));
    message.success('已作废该卡券下所有未发放、未核销券码');
  };

  const handleVoidCodes = (keys) => {
    setCodes((prev) => prev.map((c) => (keys.includes(c.key) && VOIDABLE_CODE_STATUS.includes(c.status) ? { ...c, status: 'voided' } : c)));
    setSelectedCodeKeys([]);
    message.success(`已作废 ${keys.length} 张券码`);
  };

  // 券规格文案（券码明细基础信息展示）
  const renderCouponSpec = (c) => {
    if (c.type === 'discount') {
      return `折扣率 ${c.discountRate} 折${c.maxDeduct ? `，最高抵扣 ${c.maxDeduct}` : ''}`;
    }
    if (c.type === 'fullcut') {
      const parts = [];
      if (c.cnThreshold) parts.push(`国内满 ${c.cnThreshold} 减 ${c.cnCut}`);
      if (c.usdThreshold) parts.push(`海外满 ${c.usdThreshold} 减 ${c.usdCut}`);
      return parts.join('；') || '-';
    }
    return '核销即开通绑定套餐';
  };

  // 券码明细导出
  const handleExportCodes = () => {
    if (currentCodes.length === 0) {
      message.warning('没有可导出的数据');
      return;
    }
    // TODO: 联调时替换为接口导出
    const headers = ['券码', '域节点', '发放目标', '发放目标类型', '发放目标UID', '发放时间', '核销目标', '核销目标类型', '核销目标UID', '状态'];
    const rows = currentCodes.map((c) => [
      c.code,
      getDomainNode(c.domainNode).label,
      c.target,
      c.targetType,
      c.targetUid || '-',
      c.issuedAt,
      c.usedTarget || '-',
      c.usedViaType || '-',
      c.usedUid || '-',
      CODE_STATUS[c.status].label,
    ].join(','));
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `券码明细_${codesCoupon?.name || ''}_${new Date().toLocaleDateString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  const handleExport = () => {
    if (filteredCoupons.length === 0) {
      message.warning('没有可导出的数据');
      return;
    }
    // TODO: 联调时替换为接口导出
    const headers = ['卡券ID', '卡券名称', '券类型', '绑定套餐', '已制券', '已发放', '已核销', '有效期', '状态'];
    const rows = filteredCoupons.map((c) => [
      c.id,
      c.name,
      COUPON_TYPES[c.type].label,
      getPackage(c.packageId) ? `${getPackage(c.packageId).name}（${c.packageId}）` : '所有套餐',
      c.made,
      c.issued,
      c.used,
      `${c.startDate} ~ ${c.endDate}`,
      COUPON_STATUS[resolveCouponStatus(c)].label,
    ].join(','));
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `卡券列表导出_${new Date().toLocaleDateString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  const columns = [
    {
      title: '卡券名称',
      key: 'name',
      width: 220,
      render: (_, record) => {
        const configured = countI18n(record.i18n);
        const langLabels = getI18nLabels(record.i18n);
        return (
          <div style={{ fontSize: '12px' }}>
            <div style={{ fontWeight: 500 }}>{record.name}</div>
            <div style={{ color: '#999' }}>ID: {record.id}</div>
            <Tooltip title={langLabels.length ? `已配置：${langLabels.join('、')}` : '暂未配置任何语言译文'}>
              <Button
                type="link"
                size="small"
                icon={<GlobalOutlined />}
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                onClick={() => openI18n(record)}
              >
                多语言 ({configured}/{COUPON_LANGS.length})
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: '券类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => <Tag color={COUPON_TYPES[type].color}>{COUPON_TYPES[type].label}</Tag>,
    },
    {
      title: '绑定套餐',
      key: 'package',
      width: 220,
      render: (_, record) => {
        const pkg = getPackage(record.packageId);
        if (!pkg) return <Tag color="default">所有套餐</Tag>;
        return (
          <div style={{ fontSize: '12px' }}>
            <div>{pkg.name}</div>
            <div style={{ color: '#999' }}>ID: {pkg.id}</div>
          </div>
        );
      },
    },
    { title: '已制券', dataIndex: 'made', key: 'made', width: 90 },
    { title: '已发放', dataIndex: 'issued', key: 'issued', width: 80 },
    { title: '已核销', dataIndex: 'used', key: 'used', width: 80 },
    {
      title: '有效期',
      key: 'validity',
      width: 190,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.startDate} ~</div>
          <div>{record.endDate}</div>
        </div>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const s = COUPON_STATUS[resolveCouponStatus(record)];
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 170,
      render: (_, record) => {
        const status = resolveCouponStatus(record);
        const editable = status === 'available' && record.made === 0;
        const cannotOperate = status === 'voided' || status === 'expired';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                type="link"
                size="small"
                style={{ padding: '0 4px' }}
                icon={<FileAddOutlined />}
                disabled={cannotOperate}
                onClick={() => openMake(record)}
              >
                制券
              </Button>
              <Button
                type="link"
                size="small"
                style={{ padding: '0 4px' }}
                icon={<EyeOutlined />}
                onClick={() => openCodes(record)}
              >
                券码
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={editable ? '' : '仅已制券数为 0 的可用卡券可编辑'}>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: '0 4px' }}
                  icon={<EditOutlined />}
                  disabled={!editable}
                  onClick={() => openEdit(record)}
                >
                  编辑
                </Button>
              </Tooltip>
              <Popconfirm
                title="作废该卡券下所有未发放、未核销券码，不可恢复，确认作废？"
                onConfirm={() => handleVoidCoupon(record)}
                disabled={status === 'voided'}
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  style={{ padding: '0 4px' }}
                  icon={<CloseCircleOutlined />}
                  disabled={status === 'voided'}
                >
                  作废
                </Button>
              </Popconfirm>
            </div>
          </div>
        );
      },
    },
  ];

  const codeColumns = [
    { title: '券码', dataIndex: 'code', key: 'code', width: 160 },
    {
      title: '域节点',
      key: 'domainNode',
      width: 110,
      render: (_, record) => {
        const node = getDomainNode(record.domainNode);
        return <Tag color={node.color}>{node.label}</Tag>;
      },
    },
    {
      title: '发放目标',
      key: 'target',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.target}</div>
          <div style={{ color: '#999' }}>{record.targetType}</div>
        </div>
      ),
    },
    {
      title: '发放目标(UID)',
      key: 'targetUid',
      width: 180,
      render: (_, record) => record.targetUid || <span style={{ color: '#ccc' }}>-</span>,
    },
    { title: '发放时间', dataIndex: 'issuedAt', key: 'issuedAt', width: 170 },
    {
      title: '核销目标',
      key: 'usedTarget',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.usedTarget || '-'}</div>
          <div style={{ color: '#999' }}>{record.usedViaType || '-'}</div>
        </div>
      ),
    },
    {
      title: '核销目标(UID)',
      key: 'usedUid',
      width: 180,
      render: (_, record) => record.usedUid || <span style={{ color: '#ccc' }}>-</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={CODE_STATUS[status].color}>{CODE_STATUS[status].label}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Popconfirm
          title="作废后不可恢复，确认作废该券码？"
          onConfirm={() => handleVoidCodes([record.key])}
          disabled={!VOIDABLE_CODE_STATUS.includes(record.status)}
        >
          <Button type="link" size="small" danger disabled={!VOIDABLE_CODE_STATUS.includes(record.status)}>
            作废
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ marginBottom: 8 }}>卡券管理</Title>
        <Alert
          type="info"
          showIcon
          message="卡券为平台运营专属功能：制作卡券 → 制券生成券码 →（可选）发放 / 用户凭码核销。券码即凭证，未发放券码也可被核销；券优惠由平台承担，不影响企业底价、抽佣与分润。"
        />
      </div>

      {/* 搜索区域 */}
      <Card style={{ marginBottom: '16px' }}>
        <Form form={searchForm} layout="inline" onFinish={handleSearch}>
          <Form.Item name="name" style={{ marginBottom: 0 }}>
            <Input placeholder="卡券名称" style={{ width: 170 }} allowClear />
          </Form.Item>
          <Form.Item name="id" style={{ marginBottom: 0 }}>
            <Input placeholder="卡券ID" style={{ width: 170 }} allowClear />
          </Form.Item>
          <Form.Item name="code" style={{ marginBottom: 0 }}>
            <Input placeholder="券码（反查所属卡券）" style={{ width: 190 }} allowClear />
          </Form.Item>
          <Form.Item name="type" style={{ marginBottom: 0 }}>
            <Select placeholder="券类型" style={{ width: 130 }} allowClear>
              {Object.entries(COUPON_TYPES).map(([value, cfg]) => (
                <Select.Option key={value} value={value}>{cfg.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="domainNode" style={{ marginBottom: 0 }}>
            <Select placeholder="域节点" style={{ width: 130 }} allowClear>
              {Object.entries(DOMAIN_NODES).map(([value, cfg]) => (
                <Select.Option key={value} value={value}>{cfg.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status" style={{ marginBottom: 0 }}>
            <Select placeholder="状态" style={{ width: 130 }} allowClear>
              {Object.entries(COUPON_STATUS).map(([value, cfg]) => (
                <Select.Option key={value} value={value}>{cfg.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="validity" style={{ marginBottom: 0 }}>
            <RangePicker style={{ width: 240 }} placeholder={['有效期开始', '有效期结束']} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleResetSearch}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#999', fontSize: 13 }}>
            {filters.code
              ? `券码「${filters.code}」命中 ${filteredCoupons.length} 张卡券（可点「券码」查看明细）`
              : `共 ${filteredCoupons.length} 张卡券`}
          </span>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建卡券</Button>
            <Button icon={<ExportOutlined />} onClick={handleExport}>导出</Button>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={filteredCoupons}
          scroll={{ x: 1350 }}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        />
      </Card>

      {/* 新建 / 编辑卡券 */}
      <Drawer
        title={editingCoupon ? '编辑卡券' : '新建卡券'}
        width={640}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        forceRender
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>取消</Button>
              <Button type="primary" onClick={handleSave}>保存</Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={[16, 20]}>
            <Col span={24}>
              <Form.Item
                name="name"
                label={(
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>卡券名称</span>
                    <Button
                      type="link"
                      size="small"
                      icon={<GlobalOutlined />}
                      style={{ padding: 0, height: 'auto', fontWeight: 'normal' }}
                      onClick={() => openI18n()}
                    >
                      配置多语言 (已配置 {countI18n(formI18n)}/{COUPON_LANGS.length})
                    </Button>
                  </div>
                )}
                rules={[{ required: true, message: '请输入卡券名称' }]}
              >
                <Input placeholder="请输入卡券名称（默认中文）" maxLength={50} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="type"
                label="券类型"
                rules={[{ required: true, message: '请选择券类型' }]}
              >
                <Radio.Group>
                  <Radio.Button value="exchange">兑换券</Radio.Button>
                  <Radio.Button value="discount">折扣券</Radio.Button>
                  <Radio.Button value="fullcut">满减券</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="packageId"
                label="绑定套餐"
                extra={
                  couponType === 'exchange'
                    ? '兑换券必须绑定套餐（决定核销后开通的套餐）'
                    : '不选择则对所有套餐生效；支持按套餐名称或套餐ID搜索'
                }
                rules={[{ required: couponType === 'exchange', message: '兑换券必须绑定套餐' }]}
              >
                <Select
                  placeholder="不选则所有套餐生效"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                >
                  {MOCK_PACKAGES.map((p) => (
                    <Select.Option key={p.id} value={p.id}>
                      {`${p.name}（${p.id}）`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {selectedPackage ? (
              <Col span={24}>
                <Descriptions
                  bordered
                  size="small"
                  column={2}
                  title="套餐信息"
                  style={{ marginBottom: 8 }}
                >
                  <Descriptions.Item label="套餐名称">{selectedPackage.name}</Descriptions.Item>
                  <Descriptions.Item label="套餐ID">{selectedPackage.id}</Descriptions.Item>
                  <Descriptions.Item label="售价（CNY）">{selectedPackage.priceCny}</Descriptions.Item>
                  <Descriptions.Item label="售价（USD）">{selectedPackage.priceUsd}</Descriptions.Item>
                </Descriptions>
              </Col>
            ) : (
              <Col span={24}>
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={
                    couponType === 'exchange'
                      ? '兑换券需绑定套餐，绑定后此处展示套餐名称、套餐ID与售价（CNY / USD）'
                      : '未绑定套餐：该卡券对所有套餐生效'
                  }
                />
              </Col>
            )}

            <Col span={24}>
              <Form.Item
                name="validity"
                label="有效期"
                rules={[{ required: true, message: '请选择有效期' }]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            {couponType === 'discount' && (
              <>
                <Col span={12}>
                  <Form.Item
                    name="discountRate"
                    label="折扣率（折）"
                    extra="如 8 表示按 8 折售卖"
                    rules={[{ required: true, message: '请输入折扣率' }]}
                  >
                    <InputNumber min={0.1} max={9.9} step={0.1} style={{ width: '100%' }} placeholder="如 8" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxDeduct" label="最高抵扣金额（选填）">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入" />
                  </Form.Item>
                </Col>
              </>
            )}

            {couponType === 'fullcut' && (
              <>
                <Col span={12}>
                  <Form.Item
                    name="cnThreshold"
                    label="国内满（CNY）"
                    rules={[{ required: true, message: '请输入国内门槛' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="如 100" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="cnCut"
                    label="国内减（CNY）"
                    rules={[{ required: true, message: '请输入国内减免额' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="如 20" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="usdThreshold" label="海外满（USD）">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="如 15" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="usdCut" label="海外减（USD）">
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="如 3" />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </Drawer>

      {/* 制券 */}
      <Modal
        title="制券"
        open={makeVisible}
        onOk={handleMake}
        onCancel={() => setMakeVisible(false)}
        okText="确认制券"
        cancelText="取消"
        width={560}
        centered
        forceRender
      >
        {makeCoupon && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`已制券 ${makeCoupon.made} 张、已发放 ${makeCoupon.issued} 张；制券后立即生成对应数量的券码，可再次制券累加。生成的券码即可被 C 端用户凭码核销，也可通过「发放」定向预绑目标。`}
            />
            <Descriptions
              bordered
              size="small"
              column={2}
              title="卡券规则"
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="卡券名称" span={2}>{makeCoupon.name}</Descriptions.Item>
              <Descriptions.Item label="卡券ID" span={2}>{makeCoupon.id}</Descriptions.Item>
              <Descriptions.Item label="券类型" span={2}>
                <Tag color={COUPON_TYPES[makeCoupon.type].color}>{COUPON_TYPES[makeCoupon.type].label}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="绑定套餐" span={2}>
                {getPackage(makeCoupon.packageId)
                  ? `${getPackage(makeCoupon.packageId).name}（${makeCoupon.packageId}）`
                  : '所有套餐'}
              </Descriptions.Item>
              <Descriptions.Item label="券规格" span={2}>{renderCouponSpec(makeCoupon)}</Descriptions.Item>
              <Descriptions.Item label="有效期" span={2}>
                {`${makeCoupon.startDate} ~ ${makeCoupon.endDate}`}
              </Descriptions.Item>
              <Descriptions.Item label="已制券 / 已发放">
                {`${makeCoupon.made} / ${makeCoupon.issued}`}
              </Descriptions.Item>
              <Descriptions.Item label="已核销">{makeCoupon.used}</Descriptions.Item>
              <Descriptions.Item label="状态" span={2}>
                <Tag color={COUPON_STATUS[resolveCouponStatus(makeCoupon)].color}>
                  {COUPON_STATUS[resolveCouponStatus(makeCoupon)].label}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
            <Form form={makeForm} layout="vertical">
              <Form.Item
                name="domainNode"
                label="域节点"
                extra="该批券码适用的服务器域节点（必选）"
                rules={[{ required: true, message: '请选择域节点' }]}
              >
                <Select placeholder="请选择域节点">
                  {Object.entries(DOMAIN_NODES).map(([value, cfg]) => (
                    <Select.Option key={value} value={value}>{cfg.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="count"
                label="制券数量"
                extra="输入正整数，生成对应数量的券码（未发放状态）"
                rules={[{ required: true, message: '请输入制券数量' }]}
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="请输入制券数量" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 券码明细 */}
      <Drawer
        title={`券码明细${codesCoupon ? ` · ${codesCoupon.name}` : ''}`}
        width={1000}
        open={codesVisible}
        onClose={() => setCodesVisible(false)}
      >
        {codesCoupon && (
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="卡券ID">{codesCoupon.id}</Descriptions.Item>
            <Descriptions.Item label="券类型">
              <Tag color={COUPON_TYPES[codesCoupon.type].color}>{COUPON_TYPES[codesCoupon.type].label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="绑定套餐" span={2}>
              {getPackage(codesCoupon.packageId)
                ? `${getPackage(codesCoupon.packageId).name}（${codesCoupon.packageId}）`
                : '所有套餐'}
            </Descriptions.Item>
            <Descriptions.Item label="券规格" span={2}>{renderCouponSpec(codesCoupon)}</Descriptions.Item>
            <Descriptions.Item label="有效期" span={2}>
              {`${codesCoupon.startDate} ~ ${codesCoupon.endDate}`}
            </Descriptions.Item>
            <Descriptions.Item label="已制券">{codesCoupon.made}</Descriptions.Item>
            <Descriptions.Item label="已发放 / 已核销">
              {`${codesCoupon.issued} / ${codesCoupon.used}`}
            </Descriptions.Item>
            <Descriptions.Item label="状态" span={2}>
              <Tag color={COUPON_STATUS[resolveCouponStatus(codesCoupon)].color}>
                {COUPON_STATUS[resolveCouponStatus(codesCoupon)].label}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <span style={{ color: '#999', fontSize: 13 }}>域节点：</span>
            <Select
              placeholder="全部域节点"
              style={{ width: 150 }}
              allowClear
              value={codeNodeFilter}
              onChange={(v) => setCodeNodeFilter(v)}
            >
              {Object.entries(DOMAIN_NODES).map(([value, cfg]) => (
                <Select.Option key={value} value={value}>{cfg.label}</Select.Option>
              ))}
            </Select>
          </Space>
          <Space>
            <Button icon={<ExportOutlined />} onClick={handleExportCodes}>导出</Button>
            <Popconfirm
              title={`作废选中的 ${selectedCodeKeys.length} 张未发放/未使用券码？`}
              onConfirm={() => handleVoidCodes(selectedCodeKeys)}
              disabled={selectedCodeKeys.length === 0}
            >
              <Button danger disabled={selectedCodeKeys.length === 0}>
                批量作废
              </Button>
            </Popconfirm>
          </Space>
        </div>
        <Table
          size="small"
          rowSelection={{
            selectedRowKeys: selectedCodeKeys,
            onChange: (keys) => setSelectedCodeKeys(keys),
            getCheckboxProps: (record) => ({ disabled: !VOIDABLE_CODE_STATUS.includes(record.status) }),
          }}
          columns={codeColumns}
          dataSource={currentCodes}
          scroll={{ x: 1400 }}
          pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 条记录` }}
        />
      </Drawer>

      {/* 券名称多语言配置（新建/编辑抽屉内 & 列表页券名称列快捷修改共用） */}
      <CouponNameI18nModal
        open={i18nOpen}
        name={i18nTarget ? i18nTarget.name : couponFormName}
        i18n={i18nTarget ? i18nTarget.i18n : formI18n}
        onCancel={closeI18n}
        onSave={handleI18nSave}
      />
    </div>
  );
};

export default CouponList;
